/**
 * Daily-report widget config for a project. Mirrors the API contract (see
 * ORG_ADMIN_ONLY_PROJECT_CONFIGS in project-model.js):
 *  - appdata `widgetConfig` is the org-level array of
 *    `{ name, display, display_order }`;
 *  - a project may carry its own array, same shape, at
 *    source_entity_extra_attributes.widget_config. A non-empty array REPLACES
 *    the org array for that project (no merging of entries); missing, null,
 *    empty or not an array falls back to the org array.
 *
 * The override is written in the API's widget vocabulary (`labor`,
 * `bid_items`), the one the mobile app reads too. On an AASHTOWare org the web
 * renders those two as `contractor-labor-equipment` / `item-postings`, so the
 * same rename the appdata effect applies to the org array is applied to an
 * override here. Names that are already the web ones pass through unchanged.
 *
 * Everything here is pure: the project payload is sent back to the API on a
 * project save and the API compares `widget_config` by value, so the stored
 * array must never be renamed or reordered in place.
 */
export interface WidgetConfigEntry {
  name: string;
  display: boolean;
  display_order: number;
  label?: string;
}

/** The slice of the appdata payload the widget config is read from. */
export interface WidgetConfigAppData {
  widgetConfig?: WidgetConfigEntry[] | null;
  enableAashtoIntegration?: boolean | null;
}

/** The slice of a project payload the widget config is read from. */
export interface WidgetConfigProject {
  id?: string;
  source_entity_extra_attributes?: Record<string, unknown> | null;
}

/** The web's AASHTOWare variants of the labor and bid item widgets. */
export function toAashtoWidgetConfig<T extends { name?: string }>(widgetConfig: T[]): T[] {
  return widgetConfig.map(widget => {
    if (widget.name === 'labor') {
      return { ...widget, name: 'contractor-labor-equipment' };
    }
    if (widget.name === 'bid_items') {
      return { ...widget, name: 'item-postings' };
    }
    return widget;
  });
}

/**
 * The store keeps whichever project was fetched last, and nothing at all
 * while a fetch is in flight. Only a project that is the one the screen is
 * showing may contribute an override.
 */
export function projectForRoute<T extends WidgetConfigProject>(
  project: T | null | undefined,
  projectId: string | null | undefined
): T | null {
  return !!projectId && project?.id === projectId ? project : null;
}

/**
 * Effective widget config for `project`. `appData` is the appdata payload as
 * the store holds it (its `widgetConfig` already carries the AASHTOWare
 * rename); with no usable override that array is returned as is.
 */
export function resolveWidgetConfig(
  appData: WidgetConfigAppData | null | undefined,
  project: WidgetConfigProject | null | undefined
): WidgetConfigEntry[] {
  const orgConfig = Array.isArray(appData?.widgetConfig) ? appData.widgetConfig : [];
  const override = project?.source_entity_extra_attributes?.widget_config;
  if (!Array.isArray(override)) {
    return orgConfig;
  }
  // Entries without a name cannot be rendered; an override with none left is
  // not an override.
  const entries: WidgetConfigEntry[] = override.filter(
    widget => typeof widget?.name === 'string' && widget.name !== ''
  );
  if (entries.length === 0) {
    return orgConfig;
  }
  // Weather is on every daily report (Srikanth 2026-10-03): a project list
  // that leaves it out still gets the org's weather widget, first.
  const weather = orgConfig.find(widget => widget?.name === 'weather');
  const withWeather =
    weather && !entries.some(widget => widget.name === 'weather')
      ? [{ ...weather, display: true, display_order: 0 }, ...entries]
      : entries;
  return appData?.enableAashtoIntegration ? toAashtoWidgetConfig(withWeather) : withWeather;
}
